/**
 * Useful for getting a referentially stable default value for a prop or param that won't trigger
 * useEffects or other hook dependencies.
 *
 * @example
 * function MyComponent({ someProp = frozenArr }) {
 *   useEffect(() => {
 *     // Presuming someProp is not provided and the default is used,
 *     // this effect will not run on every render because someProp is stable
 *   }, [someProp])
 * }
 *
 * @see frozenObj
 */
export const frozenArr = Object.freeze([])

export const arrayToObject = <T, U extends string | number, V>(
  array: Array<T>,
  keyFunc: (o: T) => U,
  valueFunc: (o: T) => V
): { [id in U]: V } =>
  array.reduce((acc, item) => ({ ...acc, [keyFunc(item)]: valueFunc(item) }), {} as { [id in U]: V })

export const isNotNullOrEmpty = <T>(array?: Array<T>): array is Array<T> => !!array && array.length > 0

export const isNullOrEmpty = <T>(array?: Array<T>): array is Array<T> => array == null || array.length === 0

/**
 * Finds and replaces the first item in the array, preserving the order.
 *
 * @param items array if items
 * @param predicate matcher function
 * @param item replacement item
 * @returns the array of items, replacing the match, or a copy of the original array if no match
 */
export const singleFindAndReplace = <T>(items: Array<T>, predicate: (value: T) => boolean, item: T): Array<T> => {
  const index = items.findIndex(predicate)
  if (index < 0) {
    return [...items]
  }

  const first = items.slice(0, index)
  const second = items.slice(index + 1)

  return [...first, item, ...second]
}

export const forceArray = <T>(maybeArr: T | Array<T> | null | undefined): Array<T> => {
  if (Array.isArray(maybeArr)) return maybeArr as T[]
  if (maybeArr == null) return [] as T[]
  return [maybeArr] as T[]
}

export const updateRecordValues = <K extends string | number, V>(
  record: Record<K, V>,
  updater: (key: K, value: V) => V
): Record<K, V> => {
  const keys = Object.keys as (o: Record<K, V>) => Extract<K, string | number>[]

  return keys(record).reduce(
    (newRecord, key) => ({
      ...newRecord,
      [key]: updater(key, record[key])
    }),
    {} as Record<K, V>
  )
}
